/**
 * AISquare motion presets — spring physics, easing curves, durations, and ready-
 * made transition objects that pair with `motion/react` (or framer-motion 11+).
 *
 * Use these instead of inventing per-call values so motion stays cohesive across
 * the library and across consumer apps.
 *
 *   import { motion } from "motion/react";
 *   import { springs, easings, transitions } from "@aisquare/pretty";
 *
 *   <motion.div whileTap={{ scale: 0.98 }} transition={springs.snappy} />
 *   <motion.div animate={{ y: 0 }} transition={transitions.smoothOut240} />
 *   <motion.div animate={{ x: 0 }} transition={{ duration: 0.24, ease: easings.smoothOut }} />
 *
 * All values are plain data — no library import, no runtime cost. Frozen with
 * `as const` so consumers get exact types.
 */

/* ----------------------------------------------------------------------------
 * Springs — physics-based transitions
 * ------------------------------------------------------------------------- */

export const springs = {
  /** Click feedback / mechanical press. ~120ms settle. */
  snappy: { type: "spring", stiffness: 600, damping: 28 } as const,
  /** Default UI motion — soft but responsive. ~240ms settle. */
  gentle: { type: "spring", stiffness: 280, damping: 26 } as const,
  /** Toast / confirmation pop — overshoot lightly. ~320ms settle. */
  bouncy: { type: "spring", stiffness: 500, damping: 16 } as const,
  /** Slow, deliberate — for surface mounts that should feel intentional. ~400ms settle. */
  deliberate: { type: "spring", stiffness: 180, damping: 24 } as const,
} as const;

export type SpringPreset = keyof typeof springs;

/* ----------------------------------------------------------------------------
 * Easings — cubic-bezier curves matched to the Tailwind preset
 * ------------------------------------------------------------------------- */

export const easings = {
  /** Slow in, fast out — feels deliberate. Default for surface transitions. */
  smoothOut: [0.16, 1, 0.3, 1] as const,
  /** Material-ish — fast in, fast out. For brisk feedback. */
  snappy: [0.4, 0, 0.2, 1] as const,
  /** Slight overshoot — for emphasis on a single element. */
  anticipate: [0.7, -0.2, 0.4, 1.3] as const,
  /** Linear — only for continuous motion (sweeps, beams). Avoid for discrete state changes. */
  linear: [0, 0, 1, 1] as const,
} as const;

export type EasingPreset = keyof typeof easings;

/* ----------------------------------------------------------------------------
 * Durations (seconds — matches motion's API)
 * ------------------------------------------------------------------------- */

export const durations = {
  /** Hover / focus state on interactive elements. */
  hover: 0.18,
  /** Standard surface transition (modal, drawer, suggestions). */
  surface: 0.24,
  /** Slower deliberate enter/exit. */
  deliberate: 0.32,
  /** Skeleton pulse / shimmer cadence. */
  pulse: 1.5,
  /** Spinner rotation. */
  spin: 1.0,
  /** Idle "breathing" treatments (AI thinking states). */
  breathe: 3.0,
  /** Aurora drift / mesh hue cycling. */
  aurora: 8.0,
} as const;

export type DurationPreset = keyof typeof durations;

/* ----------------------------------------------------------------------------
 * Pre-composed transitions — drop straight into `transition={...}`
 * ------------------------------------------------------------------------- */

export const transitions = {
  /** Hover lift / interactive feedback. */
  hover: { duration: durations.hover, ease: easings.smoothOut } as const,
  /** Modal / drawer / popover surface. */
  surface: { duration: durations.surface, ease: easings.smoothOut } as const,
  /** Quick brisk transition (e.g. badge count flip). */
  snappy: { duration: durations.hover, ease: easings.snappy } as const,
  /** Slower mount with overshoot. */
  deliberate: {
    duration: durations.deliberate,
    ease: easings.anticipate,
  } as const,
} as const;

/* ----------------------------------------------------------------------------
 * AI activity treatments — recommended transition + animation pairings for the
 * "AI is thinking / responding" lifecycle. Reference these in primitives that
 * sync visuals to AI state.
 * ------------------------------------------------------------------------- */

export const aiActivity = {
  /** Idle: subtle breathing on dormant interactive surfaces. */
  idle: {
    animate: { scale: [1, 0.99, 1], opacity: [1, 0.92, 1] },
    transition: {
      duration: durations.breathe,
      ease: easings.smoothOut,
      repeat: Infinity,
    },
  } as const,
  /** Thinking: slow pulse-glow on inputs while waiting on first token. */
  thinking: {
    transition: {
      duration: 2.4,
      ease: easings.smoothOut,
      repeat: Infinity,
    },
  } as const,
  /** Streaming: faster cadence to signal active output. */
  streaming: {
    transition: { duration: 1.2, ease: easings.snappy, repeat: Infinity },
  } as const,
  /** Done: soft settle. */
  done: {
    transition: springs.gentle,
  } as const,
} as const;
